import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useBrand } from "@/context/BrandContext";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { InstallButton } from "@/components/PWAInstall";
import { WhatsAppFab } from "@/components/WhatsAppFab";
import {
  Phone, Car, ShieldCheck, ArrowRight, Gift, Wallet, SquaresFour,
  CreditCard, DeviceMobile, GlobeHemisphereEast, CheckCircle, List, X, UserPlus, Lightning,
  Headset, Handshake, CurrencyDollar, ArrowsClockwise, SimCard, WhatsappLogo, Users, VideoCamera,
} from "@phosphor-icons/react";

const C = {
  bg: "#070B16", surface: "#0F1729", card: "#111a2e", green: "#10B981", orange: "#FF7A00",
  text: "#F8FAFC", sub: "#94A3B8", border: "rgba(255,255,255,0.10)", wa: "#25D366",
};
const HEAD = { fontFamily: "'Outfit', sans-serif" };
const BODY = { fontFamily: "'Manrope', sans-serif" };
const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/98eb6a0b-227b-4420-9d2a-c2a300b33bf1/images/2ac2689c1572d4c04eb8a27ebe7c48a52374368fb5126eaa343726c352cd24c2.jpeg";
const SUPPORT_WA = "https://wa.me/12029403399?text=" + encodeURIComponent("Hi! I'd like help with iYeneh.");

const PAYMENTS = [
  { label: "Card", icon: CreditCard },
  { label: "PayPal", icon: CurrencyDollar },
  { label: "Zelle", icon: Wallet },
  { label: "Cash App", icon: CurrencyDollar },
  { label: "MTN MoMo", icon: DeviceMobile },
];

const FLAGSHIP = [
  { key: "topup", title: "Send Top-up", icon: ArrowsClockwise, color: C.green, badge: "Most popular", text: "Airtime, mobile data, bills & gift cards delivered home in seconds." },
  { key: "esim", title: "eSIM", icon: SimCard, color: C.orange, text: "Instant travel data in 190+ destinations — no physical SIM needed." },
  { key: "numbers", title: "Global Numbers", icon: GlobeHemisphereEast, color: C.green, text: "Get a local number abroad and take calls & texts anywhere." },
  { key: "whatsapp", title: "WhatsApp Business", icon: WhatsappLogo, color: C.wa, text: "Reach your customers where they already chat, at scale." },
  { key: "ride", title: "iYeneh Ride", icon: Car, color: C.orange, text: "Book rides across town — or drive and earn in your city." },
  { key: "pbx", title: "Cloud PBX + SI Receptionist", icon: Users, color: C.green, text: "Extensions, IVR and an SI receptionist for your business." },
  { key: "phone", title: "iYeneh Phone System", icon: Phone, color: C.orange, text: "A full business phone system — smart routing, voicemail & desk phones." },
  { key: "meet", title: "iYeneh Meet", icon: VideoCamera, color: C.green, text: "HD video meetings, webinars & classrooms — record, share and broadcast." },
  { key: "talent", title: "iYeneh Talent", icon: Handshake, color: C.orange, text: "Hire trusted freelancers or offer your skills across the diaspora." },
];

const STEPS = [
  { n: 1, icon: UserPlus, t: "Create Your Free Account", d: "Join in seconds and receive $1 FREE calling credit." },
  { n: 2, icon: Lightning, t: "Start Connecting", d: "Use your free credit immediately or add more credit whenever you're ready." },
  { n: 3, icon: GlobeHemisphereEast, t: "Do More With iYeneh", d: "Call, message, send airtime, use eSIM, book rides, order food, access business tools and much more." },
];

const TRUST = [
  { icon: ShieldCheck, t: "Secure Payments", d: "Encrypted checkout on every transaction." },
  { icon: CreditCard, t: "Multiple Payment Options", d: "Card, PayPal, Zelle, Cash App & MTN MoMo." },
  { icon: GlobeHemisphereEast, t: "Global Connectivity", d: "Reach home and the world from any device." },
  { icon: Wallet, t: "One Account & Wallet", d: "A single balance powers every service." },
  { icon: Handshake, t: "Built for the Diaspora", d: "Designed for the African diaspora, worldwide." },
  { icon: Headset, t: "Customer Support", d: "Real help whenever you need it." },
];

const NAV = [
  { label: "SI Receptionist", to: "/receptionist" },
  { label: "Plans", to: "/wireless", children: [
    { label: "Cloud PBX & SI Receptionist", to: "/pricing" },
    { label: "Mobile Wireless — No Contract", to: "/wireless" },
    { label: "Home & Business VoIP — No Contract", to: "/wireless" },
  ] },
  { label: "Shop Devices", to: "/shop" },
  { label: "Services", to: "/services", children: [
    { label: "Book an Appointment", to: "/book" },
    { label: "Rates", to: "/rates" },
    { label: "Refer & Earn", to: "/register" },
  ] },
];

export default function Landing() {
  const brand = useBrand();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState({});
  const [menu, setMenu] = useState(false);
  useEffect(() => { api.get("/settings/public").then(({ data }) => setCfg(data || {})).catch(() => setCfg({})); }, []);

  const freeCredit = ((cfg?.signup_bonus_cents ?? 100) / 100).toFixed(0);
  const referral = ((cfg?.referral_bonus_cents ?? 200) / 100).toFixed(0);
  const [devices, setDevices] = useState([]);
  useEffect(() => { api.get("/telecom/shop/catalog").then(({ data }) => setDevices((data.products || []).filter((p) => p.featured).slice(0, 6))).catch(() => {}); }, []);

  return (
    <div style={{ ...BODY, background: C.bg, color: C.text }} className="min-h-screen overflow-x-hidden">
      {/* NAV */}
      <header className="sticky top-0 z-50 border-b bg-[#0B1220]/85 backdrop-blur-xl" style={{ borderColor: C.border }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" data-testid="nav-logo" className="animate-logo-in flex items-center gap-2">
            <img src="/icons/icon-512.png" alt="iYeneh Connect" className="h-9 w-9 sm:h-10 sm:w-10" />
            <span className="text-lg font-bold tracking-tight text-white sm:text-xl" style={{ fontFamily: "'Outfit', sans-serif" }}>iYeneh Connect</span>
          </Link>
          <nav className="hidden items-center gap-7 md:flex">
            <a href="#top" data-testid="nav-home" className="text-sm font-medium text-slate-300 hover:text-white">Home</a>
            {NAV.map((n) => n.children ? (
              <div key={n.label} className="group relative" data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {n.to ? (
                  <Link to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center gap-1 text-sm font-medium text-slate-300 hover:text-white">{n.label} <span className="text-[10px]">▾</span></Link>
                ) : (
                  <button className="flex items-center gap-1 text-sm font-medium text-slate-300 hover:text-white">{n.label} <span className="text-[10px]">▾</span></button>
                )}
                <div className="invisible absolute left-0 top-full z-50 pt-3 opacity-0 transition-opacity duration-150 group-hover:visible group-hover:opacity-100">
                  <div className="min-w-[230px] rounded-xl border border-white/10 bg-[#0F1729] p-1.5 shadow-2xl">
                    {n.children.map((c) => c.soon ? (
                      <span key={c.label} className="flex items-center justify-between rounded-lg px-3 py-2 text-sm text-slate-500">{c.label}<span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold">Soon</span></span>
                    ) : (
                      <Link key={c.label} to={c.to} data-testid={`nav-sub-${c.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="block rounded-lg px-3 py-2 text-sm text-slate-200 hover:bg-white/5">{c.label}</Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <Link key={n.label} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-sm font-medium text-slate-300 hover:text-white">{n.label}</Link>
            ))}
            <a href={SUPPORT_WA} target="_blank" rel="noreferrer" data-testid="nav-support" className="text-sm font-medium text-slate-300 hover:text-white">Support</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/login" data-testid="nav-signin" className="hidden text-sm font-semibold text-slate-200 hover:text-white sm:block">Sign In</Link>
            <Link to="/register" data-testid="nav-create-account" className="rounded-full px-4 py-2.5 text-sm font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5" style={{ background: C.orange }}>Create Free Account</Link>
            <button onClick={() => setMenu((m) => !m)} data-testid="nav-hamburger" className="text-slate-200 md:hidden" aria-label="Menu">{menu ? <X size={24} /> : <List size={24} />}</button>
          </div>
        </div>
        {menu && (
          <div className="border-t md:hidden" style={{ borderColor: C.border }} data-testid="mobile-menu">
            <nav className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-3">
              <a href="#top" onClick={() => setMenu(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-200 hover:bg-white/5">Home</a>
              {NAV.map((n) => n.children ? (
                <div key={n.label}>
                  {n.to ? (
                    <Link to={n.to} onClick={() => setMenu(false)} className="block px-3 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-400 hover:text-white">{n.label}</Link>
                  ) : (
                    <p className="px-3 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{n.label}</p>
                  )}
                  {n.children.map((c) => c.soon ? (
                    <span key={c.label} className="block rounded-lg px-5 py-2.5 text-sm text-slate-500">{c.label} — Soon</span>
                  ) : (
                    <Link key={c.label} to={c.to} onClick={() => setMenu(false)} className="block rounded-lg px-5 py-2.5 text-sm text-slate-200 hover:bg-white/5">{c.label}</Link>
                  ))}
                </div>
              ) : (
                <Link key={n.label} to={n.to} onClick={() => setMenu(false)} className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-200 hover:bg-white/5">{n.label}</Link>
              ))}
              <a href={SUPPORT_WA} target="_blank" rel="noreferrer" className="rounded-lg px-3 py-2.5 text-sm font-medium text-slate-200 hover:bg-white/5">Support</a>
              <Link to="/login" onClick={() => setMenu(false)} className="rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-200 hover:bg-white/5">Sign In</Link>
            </nav>
          </div>
        )}
      </header>

      {/* HERO */}
      <section id="top" className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-12 sm:px-6 lg:grid-cols-2 lg:py-20">
        <div>
          <div className="mb-5 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: "#10B9811a", color: C.green }}>
            <GlobeHemisphereEast size={14} weight="fill" /> One account · One wallet · A world of services
          </div>
          <h1 style={HEAD} className="text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            Connect With Home,<br /><span style={{ color: C.orange }}>Without Boundaries.</span>
          </h1>
          <p style={{ color: C.sub }} className="mt-5 max-w-xl text-base leading-relaxed sm:text-lg">
            Call, message, send airtime, get an eSIM, move wallet credit, book rides, order food, manage business communications and access powerful digital services — all with iYeneh.
          </p>
          <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <Link to="/register" data-testid="hero-create-account" className="group flex items-center justify-center gap-2 rounded-full px-7 py-4 font-bold text-white shadow-xl transition-transform hover:-translate-y-1" style={{ background: C.orange }}>
              Create Free Account <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/services" data-testid="hero-explore-services" className="flex items-center justify-center gap-2 rounded-full border-2 px-7 py-4 font-semibold transition-colors hover:bg-white/5" style={{ borderColor: C.border, color: C.text }}>
              Explore All Services
            </Link>
          </div>
          <div className="mt-6 flex flex-row items-center gap-3">
            <div data-testid="hero-free-credit" className="inline-flex min-w-0 items-center gap-2 rounded-xl border px-3.5 py-3 text-sm font-bold" style={{ borderColor: C.orange, color: C.orange, background: "#FF7A000f" }}>
              <Gift size={20} weight="fill" className="shrink-0" /> Get ${freeCredit} FREE calling credit when you join.
            </div>
            <Link to="/dialer" data-testid="hero-download-dialer" className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-full border-2 px-5 py-3.5 text-sm font-bold transition-colors hover:bg-white/5" style={{ borderColor: C.orange, color: C.orange }}>Download iYeneh Dialer</Link>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs font-medium" style={{ color: C.sub }}>
            <span className="flex items-center gap-1.5"><CheckCircle size={15} weight="fill" style={{ color: C.green }} /> No subscription required</span>
            <span className="flex items-center gap-1.5"><CheckCircle size={15} weight="fill" style={{ color: C.green }} /> Add credit from just $5</span>
            <span className="flex items-center gap-1.5"><CheckCircle size={15} weight="fill" style={{ color: C.green }} /> Multiple ways to pay</span>
            <span className="flex items-center gap-1.5"><CheckCircle size={15} weight="fill" style={{ color: C.green }} /> Global connectivity</span>
          </div>
        </div>
        <div className="relative w-full lg:justify-self-end" style={{ maxWidth: 520 }}>
          <a href="https://demo.iyeneh.com" target="_blank" rel="noopener noreferrer" data-testid="hero-banner-link"
             className="group block overflow-hidden rounded-[2rem] shadow-2xl transition-transform duration-300 hover:-translate-y-1">
            <img src="/homepage-banner.png"
                 alt="iYeneh SI Receptionist — Never miss another customer. Answers calls, captures leads, books appointments 24/7. Hear the live demo at demo.iYeneh.com"
                 className="w-full transition-transform duration-500 group-hover:scale-[1.02]" style={{ aspectRatio: "1122 / 1402" }} />
          </a>
          <p className="mt-2.5 text-center text-xs" style={{ color: C.sub }}>
            Hear it live at <span style={{ color: C.orange, fontWeight: 700 }}>demo.iYeneh.com</span> — the real SI receptionist that answers, qualifies and books 24/7.
          </p>
        </div>
      </section>

      {/* APPOINTMENT / LEAD CAPTURE */}
      <section className="mx-auto max-w-6xl px-4 pb-4 sm:px-6" data-testid="home-appointment-section">
        <div className="rounded-3xl border p-6 sm:p-9" style={{ borderColor: C.border, background: "linear-gradient(160deg,#FF7A0012,transparent)" }}>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: "#FF7A001a", color: C.orange }}>
                <Phone size={14} weight="fill" /> Never miss another customer call
              </div>
              <h2 style={HEAD} className="mt-3 text-2xl font-black tracking-tight sm:text-3xl">Book an appointment or get an SI receptionist</h2>
              <p className="mt-2 max-w-xl text-sm sm:text-base" style={{ color: C.sub }}>
                Tell us about your business and we'll recommend the right iYeneh solution — or let our SI receptionist answer, qualify and book for you 24/7.
              </p>
            </div>
            <a href="tel:+12029805224" data-testid="home-call-iyeneh" className="flex items-center gap-2 rounded-full px-5 py-3 font-bold text-white shadow-lg" style={{ background: C.green }}>
              <Phone size={17} weight="fill" /> Call iYeneh: +1 (202) 980-5224
            </a>
          </div>
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <Link to="/book" data-testid="home-book-appointment" className="group rounded-2xl border p-5 transition-transform hover:-translate-y-1" style={{ borderColor: C.border, background: C.card }}>
              <p style={HEAD} className="font-bold text-white">Book a Service Appointment</p>
              <p className="mt-1 text-sm" style={{ color: C.sub }}>Pick a time and we'll confirm it instantly.</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: C.orange }}>Start <ArrowRight size={15} weight="bold" className="transition-transform group-hover:translate-x-1" /></span>
            </Link>
            <Link to="/receptionist" data-testid="home-need-receptionist" className="group rounded-2xl border p-5 transition-transform hover:-translate-y-1" style={{ borderColor: C.border, background: C.card }}>
              <p style={HEAD} className="font-bold text-white">I Need an SI Receptionist</p>
              <p className="mt-1 text-sm" style={{ color: C.sub }}>Answers every call, books and captures leads.</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: C.orange }}>Get started <ArrowRight size={15} weight="bold" className="transition-transform group-hover:translate-x-1" /></span>
            </Link>
            <Link to="/book" data-testid="home-other-services" className="group rounded-2xl border p-5 transition-transform hover:-translate-y-1" style={{ borderColor: C.border, background: C.card }}>
              <p style={HEAD} className="font-bold text-white">Ask About Other iYeneh Services</p>
              <p className="mt-1 text-sm" style={{ color: C.sub }}>PBX, numbers, follow-up, eSIM and more.</p>
              <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold" style={{ color: C.orange }}>Enquire <ArrowRight size={15} weight="bold" className="transition-transform group-hover:translate-x-1" /></span>
            </Link>
          </div>
          <p className="mt-4 text-xs" style={{ color: C.sub }}>SI Appointment Receptionist available at <b className="text-white">Extension 10001</b>.</p>
        </div>
      </section>

      {/* PAY YOUR WAY */}
      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        <div className="rounded-3xl border p-7 sm:p-10" style={{ background: C.surface, borderColor: C.border }} data-testid="pay-your-way">
          <div className="text-center">
            <h2 style={HEAD} className="text-2xl font-extrabold sm:text-3xl">Pay Your Way</h2>
            <p className="mx-auto mt-2 max-w-lg text-base" style={{ color: C.sub }}>Choose the payment method that works best where you are.</p>
          </div>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            {PAYMENTS.map((p) => (
              <div key={p.label} data-testid={`pay-${p.label.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-center gap-2 rounded-full border bg-[#111a2e] px-5 py-3 text-sm font-bold" style={{ borderColor: C.border }}>
                <p.icon size={18} weight="fill" style={{ color: C.green }} /> {p.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        <h2 style={HEAD} className="text-center text-2xl font-extrabold tracking-tight sm:text-3xl">Connecting Home Is This Easy</h2>
        <div className="mt-8 grid gap-5 md:grid-cols-3">
          {STEPS.map((s) => (
            <div key={s.n} data-testid={`step-${s.n}`} className="rounded-2xl border bg-[#111a2e] p-6" style={{ borderColor: C.border }}>
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full text-base font-black text-white" style={{ background: C.green }}>{s.n}</span>
                <s.icon size={24} weight="duotone" style={{ color: C.orange }} />
              </div>
              <h3 style={HEAD} className="mt-4 text-lg font-extrabold">{s.t}</h3>
              <p className="mt-2 text-sm" style={{ color: C.sub }}>{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FLAGSHIP SERVICES */}
      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        <div className="text-center">
          <div className="mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: "#10B9811a", color: C.green }}>
            <Wallet size={14} weight="fill" /> One balance powers everything
          </div>
          <h2 style={HEAD} className="text-2xl font-extrabold tracking-tight sm:text-4xl">One Account. One Wallet. Every Service.</h2>
          <p className="mx-auto mt-3 max-w-2xl text-base sm:text-lg" style={{ color: C.sub }}>Nine flagship services on a single wallet — plus 20+ more tools to connect, pay, move and do business.</p>
        </div>
        <div className="mt-9 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FLAGSHIP.map((e) => (
            <Link key={e.key} to="/services" data-testid={`flagship-${e.key}`} className="group relative flex flex-col rounded-2xl border bg-[#111a2e] p-6 transition-all hover:-translate-y-1 hover:shadow-xl" style={{ borderColor: C.border }}>
              {e.badge && <span className="absolute right-4 top-4 rounded-full px-2.5 py-0.5 text-[10px] font-bold text-white" style={{ background: C.orange }}>{e.badge}</span>}
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl text-white" style={{ background: e.color }}><e.icon size={24} weight="fill" /></div>
              <h3 style={HEAD} className="text-xl font-extrabold">{e.title}</h3>
              <p className="mt-2 flex-1 text-sm" style={{ color: C.sub }}>{e.text}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: e.color }}>Learn more <ArrowRight size={15} weight="bold" className="transition-transform group-hover:translate-x-1" /></span>
            </Link>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to="/services" data-testid="explore-all-services" className="inline-flex items-center gap-2 rounded-full border-2 px-7 py-3.5 font-bold transition-colors hover:bg-white/5" style={{ borderColor: C.border, color: C.text }}>
            Explore All Services <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </section>

      {devices.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6" data-testid="home-popular-devices">
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="mb-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: "#FF7A001a", color: C.orange }}><DeviceMobile size={14} weight="fill" /> Popular devices</div>
              <h2 style={HEAD} className="text-2xl font-extrabold tracking-tight sm:text-3xl">Plug &amp; Play Phone Devices</h2>
            </div>
            <Link to="/shop" data-testid="popular-shop-all" className="hidden whitespace-nowrap text-sm font-bold sm:inline-flex" style={{ color: C.orange }}>Shop all →</Link>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {devices.map((p) => (
              <Link key={p.id} to="/shop" data-testid={`popular-device-${p.id}`} className="group rounded-2xl border bg-[#111a2e] p-3 transition-transform hover:-translate-y-1" style={{ borderColor: C.border }}>
                <div className="aspect-square w-full overflow-hidden rounded-xl bg-[#0d1424]">{p.image ? <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform group-hover:scale-105" /> : null}</div>
                <p className="mt-2 line-clamp-1 text-xs font-semibold text-white">{p.name}</p>
                <p className="text-xs font-bold" style={{ color: C.orange }}>{money(p.price_cents, "USD")}</p>
              </Link>
            ))}
          </div>
          <div className="mt-6 text-center sm:hidden">
            <Link to="/shop" className="inline-flex items-center gap-2 rounded-full border-2 px-6 py-3 text-sm font-bold" style={{ borderColor: C.orange, color: C.orange }}>Shop all devices <ArrowRight size={15} weight="bold" /></Link>
          </div>
        </section>
      )}

      {/* $1 FREE CONVERSION */}
      <section className="px-4 py-14 sm:px-6" style={{ background: C.green }}>
        <div className="mx-auto max-w-3xl text-center text-white" data-testid="free-credit-section">
          <p style={HEAD} className="text-sm font-bold uppercase tracking-[0.2em] text-white/80">Start Free. Experience iYeneh.</p>
          <h2 style={HEAD} className="mt-3 text-3xl font-black tracking-tight sm:text-5xl">Your First Call Is On Us.</h2>
          <p className="mx-auto mt-4 max-w-xl text-white/85">Create your free iYeneh account and get ${freeCredit} FREE calling credit to experience the platform.</p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link to="/register" data-testid="free-credit-cta" className="rounded-full px-8 py-4 font-bold text-white shadow-xl transition-transform hover:-translate-y-1" style={{ background: C.orange }}>Get My ${freeCredit} Free Credit</Link>
            <Link to="/rates" data-testid="free-credit-rates" className="rounded-full border-2 border-white/40 px-8 py-4 font-semibold text-white transition-colors hover:bg-white/10">View Calling Rates</Link>
          </div>
          <p className="mt-4 text-sm text-white/70">No subscription required. Add credit starting from just $5 whenever you're ready.</p>
        </div>
      </section>

      {/* PUBLIC RATES CTA */}
      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        <div className="flex flex-col items-center gap-5 rounded-3xl border p-7 text-center sm:flex-row sm:justify-between sm:p-9 sm:text-left" style={{ background: C.surface, borderColor: C.border }} data-testid="rates-cta">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl text-white" style={{ background: C.green }}><Phone size={24} weight="fill" /></div>
            <div>
              <h3 style={HEAD} className="text-xl font-extrabold">Transparent international calling rates</h3>
              <p className="mt-1 text-sm" style={{ color: C.sub }}>Check the cost to call any destination — no account needed.</p>
            </div>
          </div>
          <Link to="/rates" data-testid="see-rates" className="whitespace-nowrap rounded-full px-7 py-3.5 font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5" style={{ background: C.orange }}>See Calling Rates</Link>
        </div>
      </section>

      {/* INSTALL PWA */}
      <section className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        <div className="grid items-center gap-8 rounded-3xl border p-7 sm:p-10 lg:grid-cols-2" style={{ background: "linear-gradient(135deg,#0F1729,#0B1220)", borderColor: C.border }} data-testid="install-section">
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold" style={{ background: "#FF7A001a", color: C.orange }}><DeviceMobile size={14} weight="fill" /> Installable app</div>
            <h2 style={HEAD} className="text-2xl font-extrabold sm:text-4xl">Take iYeneh Everywhere.</h2>
            <p className="mt-3 max-w-lg text-base" style={{ color: C.sub }}>Install iYeneh directly on your phone, tablet or computer for faster access to calls, messages, wallet, top-ups, rides, business tools and all your iYeneh services.</p>
            <div className="mt-6">
              <InstallButton testid="install-iyeneh" className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-bold text-white shadow-xl transition-transform hover:-translate-y-1" style={{ background: C.orange }}>Install iYeneh</InstallButton>
            </div>
            <p className="mt-3 text-xs" style={{ color: C.sub }}>No App Store download required where browser installation is supported.</p>
          </div>
          <div className="grid grid-cols-3 gap-4">
            {[{ i: DeviceMobile, t: "Phone" }, { i: SquaresFour, t: "Tablet" }, { i: CreditCard, t: "Desktop" }].map((d) => (
              <div key={d.t} className="flex flex-col items-center gap-2 rounded-2xl border bg-[#111a2e] p-5" style={{ borderColor: C.border }}>
                <d.i size={30} weight="duotone" style={{ color: C.green }} />
                <span className="text-xs font-semibold" style={{ color: C.sub }}>{d.t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REFERRAL */}
      <section className="mx-auto max-w-6xl px-4 pb-4 sm:px-6">
        <div className="flex flex-col items-center gap-5 rounded-3xl p-8 text-center sm:flex-row sm:text-left" style={{ background: C.green, color: "#fff" }} data-testid="referral-card">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl" style={{ background: C.orange }}><Gift size={28} weight="fill" /></div>
          <div className="flex-1">
            <h3 style={HEAD} className="text-2xl font-extrabold">Give ${referral}. Get ${referral}. Keep Connecting.</h3>
            <p className="mt-1 text-white/80">Invite friends and family to iYeneh and earn credit when they qualify under our referral program.</p>
          </div>
          <Link to="/register" data-testid="referral-cta" className="rounded-full px-7 py-3.5 font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5" style={{ background: C.orange }}>Invite &amp; Earn</Link>
        </div>
      </section>

      {/* TRUST */}
      <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
        <h2 style={HEAD} className="text-center text-2xl font-extrabold tracking-tight sm:text-3xl">Built To Keep You Connected</h2>
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TRUST.map((t) => (
            <div key={t.t} data-testid={`trust-${t.t.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="flex items-start gap-4 rounded-2xl border bg-[#111a2e] p-5" style={{ borderColor: C.border }}>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ background: "#10B9811a", color: C.green }}><t.icon size={22} weight="fill" /></div>
              <div><p style={HEAD} className="font-extrabold">{t.t}</p><p className="mt-0.5 text-sm" style={{ color: C.sub }}>{t.d}</p></div>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-4 py-16 text-center text-white sm:px-6" style={{ background: "#0B1220" }} data-testid="final-cta">
        <h2 style={HEAD} className="mx-auto max-w-2xl text-3xl font-black tracking-tight sm:text-5xl">Home Is Just a Call Away.</h2>
        <p className="mx-auto mt-4 max-w-xl text-white/60">Create your free iYeneh account, get ${freeCredit} calling credit, install iYeneh on your device, and discover everything the platform can do.</p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link to="/register" data-testid="final-create-account" className="rounded-full px-8 py-4 font-bold text-white shadow-xl transition-transform hover:-translate-y-1" style={{ background: C.orange }}>Create Free Account</Link>
          <InstallButton testid="final-install" className="inline-flex items-center gap-2 rounded-full border-2 border-white/40 px-8 py-4 font-semibold text-white transition-colors hover:bg-white/10">Install iYeneh</InstallButton>
          <Link to="/services" data-testid="final-services" className="text-sm font-semibold text-white/70 underline-offset-4 hover:text-white hover:underline">Explore All Services</Link>
        </div>
        <p className="mt-5 text-sm text-white/45">No subscription • Multiple payment options • Global connectivity • Add credit when you're ready</p>
      </section>

      {/* FOOTER */}
      <footer className="border-t bg-[#0B1220]" style={{ borderColor: C.border }}>
        <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 sm:px-6 sm:grid-cols-3 lg:grid-cols-5">
          <FooterCol title="iYeneh" links={[["About", "/services"], ["All Services", "/services"], ["How It Works", "/#top"]]} />
          <FooterCol title="Connect" links={[["Calling Rates", "/rates"], ["eSIM", "/register"], ["Top-Ups", "/register"], ["Global Numbers", "/register"]]} />
          <FooterCol title="Business" links={[["SI Receptionist", "/receptionist"], ["Reseller", "/register"], ["Cloud PBX", "/services"], ["WhatsApp Business", "/services"]]} />
          <FooterCol title="Support" links={[["Help", SUPPORT_WA, true], ["Contact", SUPPORT_WA, true]]} />
          <div>
            <p style={HEAD} className="mb-3 text-sm font-bold">Install</p>
            <InstallButton testid="footer-install" icon={false} className="rounded-full px-4 py-2 text-sm font-bold text-white" style={{ background: C.orange }}>Install iYeneh</InstallButton>
          </div>
        </div>
        <div className="border-t px-4 py-5 text-center text-xs sm:px-6" style={{ borderColor: C.border, color: C.sub }}>
          © {new Date().getFullYear()} {brand.legal_name || brand.brand_name} · <Link to="/privacy" className="hover:text-white">Privacy</Link> · <Link to="/terms" className="hover:text-white">Terms</Link> · <Link to="/data-deletion" className="hover:text-white">Data Deletion</Link> · <Link to="/login" className="hover:text-white">Sign In</Link> · <Link to="/register" className="hover:text-white">Create Account</Link>
        </div>
      </footer>
      <WhatsAppFab />
    </div>
  );
}

const FooterCol = ({ title, links }) => (
  <div>
    <p style={HEAD} className="mb-3 text-sm font-bold">{title}</p>
    <ul className="space-y-2 text-sm" style={{ color: C.sub }}>
      {links.map(([label, to, ext]) => (
        <li key={label}>
          {ext ? <a href={to} target="_blank" rel="noreferrer" className="hover:text-white">{label}</a> : <Link to={to} className="hover:text-white">{label}</Link>}
        </li>
      ))}
    </ul>
  </div>
);
